import { useCallback, useEffect, useState, useRef } from 'react';
import {
  ReactFlow,
  ReactFlowProvider,
  Background,
  useNodesState,
  useEdgesState,
  Panel,
  useReactFlow,
  BackgroundVariant,
  type Node,
  type Edge,
} from '@xyflow/react';
import '@xyflow/react/dist/base.css';
import { RiAddLine, RiSubtractLine, RiFullscreenLine, RiLoader2Line } from '@remixicon/react';
import { Button } from '@/components/ui/button';
import TableNode from '@/components/table-node';
import SchemaEdge from '@/components/schema-edge';
import { api } from '@/lib/api';
import { toast } from 'sonner';
import { CollaborativeCursor } from '@/components/collaborative-cursor';
import { SchemaAIAssistant } from '@/components/schema-ai-assistant';
import type { CursorPosition } from '@/types/collaboration';

const nodeTypes = {
  tableNode: TableNode,
};

const edgeTypes = {
  custom: SchemaEdge,
};

interface SchemaVisualizerInnerProps {
  databaseId: string;
  broadcastCursorMove?: (position: CursorPosition) => void;
  activeCursors?: Array<{
    userId: string;
    userName: string;
    userImage: string | null;
    position: CursorPosition;
  }>;
}

function SchemaVisualizerInner({
  databaseId,
  broadcastCursorMove,
  activeCursors = []
}: SchemaVisualizerInnerProps) {
  const [nodes, setNodes, onNodesChange] = useNodesState([]);
  const [edges, setEdges, onEdgesChange] = useEdgesState([]);
  const [isLoading, setIsLoading] = useState(true);
  const { fitView, zoomIn, zoomOut } = useReactFlow();
  const flowContainerRef = useRef<HTMLDivElement>(null);

  const onFitView = useCallback(() => {
    fitView({ padding: 0.2, duration: 400 });
  }, [fitView]);

  // Handle mouse move for cursor broadcasting
  const handleMouseMove = useCallback((event: React.MouseEvent<HTMLDivElement>) => {
    if (!broadcastCursorMove || !flowContainerRef.current) return;

    const rect = flowContainerRef.current.getBoundingClientRect();
    const x = ((event.clientX - rect.left) / rect.width) * 100;
    const y = ((event.clientY - rect.top) / rect.height) * 100;

    // Clamp values
    const clampedX = Math.max(0, Math.min(100, x));
    const clampedY = Math.max(0, Math.min(100, y));

    broadcastCursorMove({ x: clampedX, y: clampedY });
  }, [broadcastCursorMove]);

  useEffect(() => {
    loadSchema();
  }, [databaseId]);

  const loadSchema = async () => {
    try {
      setIsLoading(true);
      const schema = await api.getDatabaseSchema(databaseId);

      const newNodes: Node[] = [];
      const newEdges: Edge[] = [];

      schema.tables.forEach((table, index) => {
        const x = (index % 3) * 350;
        const y = Math.floor(index / 3) * 300;

        newNodes.push({
          id: table.name,
          type: 'tableNode',
          position: { x, y },
          data: {
            label: table.name,
            fields: table.fields,
          },
        });

        table.fields.forEach((field) => {
          if (field.isForeign && field.foreignTable) {
            newEdges.push({
              id: `${table.name}-${field.name}-${field.foreignTable}`,
              source: field.foreignTable,
              target: table.name,
              sourceHandle: field.foreignKey || 'id',
              targetHandle: field.name,
              type: 'custom',
            });
          }
        });
      });

      setNodes(newNodes);
      setEdges(newEdges);

      setTimeout(() => {
        fitView({ padding: 0.2, duration: 400 });
      }, 100);
    } catch (error: any) {
      toast.error('Failed to load schema');
      console.error('Schema load error:', error);
    } finally {
      setIsLoading(false);
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="text-center">
          <RiLoader2Line className="h-8 w-8 animate-spin mx-auto mb-3 text-primary" />
          <p className="text-sm text-muted-foreground">Loading schema...</p>
        </div>
      </div>
    );
  }

  return (
    <div
      ref={flowContainerRef}
      className="w-full h-full relative"
      onMouseMove={handleMouseMove}
    >
      <ReactFlow
        nodes={nodes}
        edges={edges}
        onNodesChange={onNodesChange}
        onEdgesChange={onEdgesChange}
        nodeTypes={nodeTypes}
        edgeTypes={edgeTypes}
        fitView
        minZoom={0.1}
        maxZoom={4}
        defaultEdgeOptions={{
          type: 'custom',
        }}
        style={
          {
            '--xy-background-pattern-dots-color-default': 'hsl(var(--border))',
            '--xy-edge-stroke-width-default': 1.5,
            '--xy-edge-stroke-default': 'hsl(var(--foreground))',
            '--xy-edge-stroke-selected-default': 'hsl(var(--foreground))',
          } as React.CSSProperties
        }
        proOptions={{ hideAttribution: true }}
      >
        <Background variant={BackgroundVariant.Dots} gap={20} size={2} />

        {/* AI Assistant */}
        <Panel position="top-right" className="mt-2 mr-2">
          <SchemaAIAssistant databaseId={databaseId} />
        </Panel>

        <Panel position="bottom-right" className="inline-flex -space-x-px rounded-md shadow-sm">
          <Button
            variant="outline"
            size="icon"
            className="text-muted-foreground/80 hover:text-muted-foreground rounded-none shadow-none first:rounded-s-lg last:rounded-e-lg size-10 focus-visible:z-10 bg-card"
            onClick={() => zoomIn({ duration: 200 })}
            aria-label="Zoom in"
            title="Zoom in"
          >
            <RiAddLine className="size-5" aria-hidden="true" />
          </Button>
          <Button
            variant="outline"
            size="icon"
            className="text-muted-foreground/80 hover:text-muted-foreground rounded-none shadow-none first:rounded-s-lg last:rounded-e-lg size-10 focus-visible:z-10 bg-card"
            onClick={() => zoomOut({ duration: 200 })}
            aria-label="Zoom out"
            title="Zoom out"
          >
            <RiSubtractLine className="size-5" aria-hidden="true" />
          </Button>
          <Button
            variant="outline"
            size="icon"
            className="text-muted-foreground/80 hover:text-muted-foreground rounded-none shadow-none first:rounded-s-lg last:rounded-e-lg size-10 focus-visible:z-10 bg-card"
            onClick={onFitView}
            aria-label="Fit view"
            title="Fit to view"
          >
            <RiFullscreenLine className="size-5" aria-hidden="true" />
          </Button>
        </Panel>
      </ReactFlow>

      {/* Collaborative Cursors */}
      {activeCursors.map((cursor) => (
        <div
          key={cursor.userId}
          className="absolute pointer-events-none z-50"
          style={{
            left: `${cursor.position.x}%`,
            top: `${cursor.position.y}%`,
          }}
        >
          <CollaborativeCursor
            userId={cursor.userId}
            userName={cursor.userName}
            userAvatar={cursor.userImage}
            position={{ x: 0, y: 0 }}
          />
        </div>
      ))}
    </div>
  );
}

interface SchemaVisualizerProps {
  databaseId: string;
  broadcastCursorMove?: (position: CursorPosition) => void;
  activeCursors?: Array<{
    userId: string;
    userName: string;
    userImage: string | null;
    position: CursorPosition;
  }>;
}

export default function SchemaVisualizer({
  databaseId,
  broadcastCursorMove,
  activeCursors
}: SchemaVisualizerProps) {
  return (
    <ReactFlowProvider>
      <SchemaVisualizerInner
        databaseId={databaseId}
        broadcastCursorMove={broadcastCursorMove}
        activeCursors={activeCursors}
      />
    </ReactFlowProvider>
  );
}
